import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// tutor-2 (D.2): el boton "Tutor" del encabezado.
import BotonTutor from '../components/tutor/BotonTutor';
import { tutorVisible, useTutorDeLaSesion } from '../lib/tutorDeLaSesion';
import api, { isNetworkShapedError, isOfflineShapedError } from '../lib/api';
import { BarChart3, BookOpen, Play, RotateCw, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useScreenData } from '../hooks/useScreenData';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { isExplicitlyOffline } from '../lib/offline/networkVerdict';
import { IS_APP_BUILD } from '../lib/buildTarget';
// F6.8 T3 — el switch del corte de ESTA pantalla. Import directo al módulo de
// flags (no al barril `lib/brain`): es la misma superficie estática liviana y
// deja el barril intacto.
import { analyticsRunsLocally } from '../lib/brain/cutover';
import {
  EncabezadoDePagina, FilaDeResumen, NumeroDeResumen, PestanasDeDosBotones, RecuadroDeEstado,
} from '../components/alumno';
import { CAJA_ICONO, TRAZO_ICONO } from '../components/panel/anatomia';
import {
  ChipConGlobo, EsqueletoDeAnaliticas, EsqueletoDelDetalle, ResultadosDeExamen, TarjetaDeAnaliticas,
  TarjetaDeReadiness, TarjetaDeTema,
} from '../components/analiticas/PiezasDeAnaliticas';
import StickyBackPill from '../components/StickyBackPill';
// t7-2b (E3, pendiente 13): el glifo del tipo (hoja, video o audio) en la lista y en la
// cabecera del detalle, sin caja.
import { GlifoDeMaterial } from '../components/IconoDeMaterial';
import { tipoDeMaterial } from '../lib/tipoDeMaterial';
import { useAuth } from '../contexts/AuthContext';
import { useReportarCapa, useScrollportRaiz } from '../lib/pager/pane';
import { useEdgeSwipeBack } from '../lib/useEdgeSwipeBack';
import { examDeliveriesFromWarehouse } from '../lib/warehouse/screenSources';
import { mergeExamDeliveries } from '../lib/warehouse/examAnalyticsMerge';
// PAGER-1 — el contrato montada/activa (la doctrina completa vive en el módulo).
import { useRaizActiva } from '../lib/pager/activacion';

// pantallas-alumno (F): la pagina de Analiticas, hasta 1040 y centrada (4.1 del encargo). La tarjeta
// de la lista, Readiness (con la regla del Panel), la tarjeta de tema, Exam Results y los esqueletos
// viven en `components/analiticas/PiezasDeAnaliticas.js`.
const PAGINA = 'mx-auto w-full max-w-[1040px] flex flex-col gap-5 md:gap-6';

// Fetcher del overview — cacheado bajo 'analytics-overview' (regla de frescura
// F4.3b; al salir de una sesión de estudio App.js invalida esta key). El detalle
// por documento NO se cachea: es una acción explícita del usuario (clic en la
// card) y siempre debe traer la verdad del momento.
// F6.7b post-smoke — con DICTAMEN explícito de sin-red la petición está
// condenada. Antes esperaba a que se rindiera para mostrar su aviso; ahora se
// rinde de entrada. (F6.8 T3: primero se le pregunta a la bodega; esto es lo
// que queda cuando la bodega tampoco puede.) `offlineShapedError()` no lleva
// `response`, así que las dos clasificaciones lo tratan igual: la bodega lo ve
// como forma-de-red y el render lo ve como sin conexión (F6.8 EH-2), o sea el
// ESTADO 1, sin toast. Al volver la señal ya se re-pide sola (efecto de abajo).
function offlineShapedError() {
  return new Error('offline-verdict');
}

// ── F6.8 T3 — ANALYTICS SE RESUELVE DESDE LA BODEGA ─────────────────────────
//
// Con ANALYTICS_LOCAL_CUTOVER encendido, esta pantalla gana las MISMAS DOS
// PUERTAS que sus hermanas (Study Center / Study Log / Dashboard):
//
//   1. ATAJO POR DICTAMEN — con veredicto explícito de sin-red la petición está
//      condenada y axios no fija timeout propio (api.js:18): se pregunta a la
//      bodega de entrada, sin lanzarla.
//   2. CAÍDA POR FORMA-DE-RED — el fetch vivo cayó con `isNetworkShapedError`:
//      la bodega responde en su lugar.
//
// El dato de bodega entra COMO DATO FRESCO, resolviendo el MISMO Promise del
// fetcher: jamás como segundo render encima de otro (stale-then-swap prohibido,
// censo §F). Un fetch vivo EXITOSO se comporta exactamente como hoy y la bodega
// ni se toca.
//
// `null` del ensamblador = la bodega no puede servir esta pantalla (instalación
// nueva, documento jamás bajado, dueño ajeno, web). Entonces NO se inventa nada:
// se sigue al camino de error de siempre y el render decide qué estado toca según
// la frontera de F6.8 EH-2 — sin conexión (estado 1) si el fallo nunca llegó al
// servidor, o servidor caído (estado 3, con Reintentar) si contestó mal.
//
// `opts.plan` NO viaja: el fetcher de hoy no obtiene el plan por ningún lado (el
// servidor lo deriva del usuario autenticado) y ninguna superficie pinta el
// `plan` de estas dos respuestas. El driver lo default-ea a 'free' y ese campo
// no llega a ningún píxel.

// Import DINÁMICO, el mismo patrón del resto del carril: la web jamás pide ese
// chunk y en la app solo se carga cuando una de las dos puertas se abre.
const loadSources = () => import('../lib/warehouse/screenSources');
// APP-1 B5 (#402): el compositor de los tres totales es PURO y vive en el cerebro,
// al lado de la doctrina que obedece. Import dinámico, igual que su vecino: en la
// web esta rama no se pisa nunca y el chunk no se pide.
const loadReads = () => import('../lib/brain/analyticsReads');

// ── TANDA 15 (#192) — DE DÓNDE VINO EL OVERVIEW, POR PROCEDENCIA ────────────
//
// LA LISTA DE LA BODEGA ES UN SUBCONJUNTO, NO UN CENSO: sólo conoce los
// documentos DESCARGADOS a este aparato y no bloqueados. Un documento que
// existe en el servidor pero que este teléfono nunca bajó falta de ella. El
// cerrojo 2 lee ausencia como muerte, así que sobre una lista parcial echaría
// al usuario de un detalle perfectamente vivo (medido en el remate de esta
// tanda antes de empujar). Ausencia en una lista parcial no prueba nada.
//
// MISMO IDIOMA QUE G-3 (`DETALLE_DESDE_BODEGA`, unas líneas más abajo) y por la
// misma razón: la marca vive en un WeakSet de este módulo, no en un campo del
// objeto, así que las dos respuestas —la viva y la de bodega— siguen saliendo
// del fetcher POR IDENTIDAD, tal cual su fuente las devolvió. El fetcher es el
// único que sabe de dónde vino.
const OVERVIEW_DESDE_BODEGA = new WeakSet();

function marcarOverviewDeBodega(overview) {
  if (overview && typeof overview === 'object') OVERVIEW_DESDE_BODEGA.add(overview);
  return overview;
}

// t7-1c (#480) -- LAS CARDS DEL PROFE EN LAS ANALÍTICAS DEL ALUMNO.
//
// El servidor manda las cards que el alumno estudió en `pub_cards`, una llave
// APARTE de `doc_cards` que SÓLO existe si hay alguna: `doc_cards` nunca las trae,
// así que la app publicada y una pestaña con el bundle viejo (que pintan
// `doc_cards` tal cual) no las ven. Cada entrada lleva los MISMOS campos de una
// entrada de documento (`id` es el documento de la card y `filename` su nombre) más
// `publicacion_id`, `nombre` y `grupo_nombre`, y la lista llega ordenada con la
// llave de `doc_cards`.
// EN LA WEB se unen a `doc_cards`. Dos cards del mismo documento llegan con el
// MISMO `id`, así que cada una se re-etiqueta con la convención del Centro de
// Estudio ("pub:<publicacion_id>", el `PREFIJO_CARD` de `StudyCenter.js`) y su
// documento se guarda aparte en `doc_id`. La lista unida se ordena como la ordenaba
// el servidor antes de separarlas (el `sorted(..., reverse=True)` de Python, que es
// estable): `last_studied` descendente (sin fecha cuenta como cadena vacía) y, en
// empate, el orden de llegada, primero `doc_cards` y luego `pub_cards`. El empate
// se decide por el índice y no por la estabilidad del motor. Con eso la tarjeta, su
// `key`, la selección, el cerrojo 2 y los botones de estudiar (`/study?doc=pub:<id>`,
// que el Centro trata como `?pub=`) siguen siendo las líneas de siempre.
// EN LA APP tambien se unen desde t7-3 (A6, #557; firmado el 29-sep): la app pinta cada
// card estudiada como la web. (Antes se descartaban: la app no pintaba cards.)
// En los dos casos la llave `pub_cards` sale del objeto. Sin ella devuelve EL MISMO
// objeto: la respuesta de siempre, por identidad, byte a byte.
const PREFIJO_CARD = 'pub:';
const esIdDeCard = (id) => typeof id === 'string' && id.startsWith(PREFIJO_CARD);
const llaveDeOrden = (e) => (e && e.last_studied) || '';

function conLasCards(overview) {
  if (!overview || typeof overview !== 'object'
    || !Object.prototype.hasOwnProperty.call(overview, 'pub_cards')) return overview;
  const { pub_cards: pubCards, ...resto } = overview;
  const cards = Array.isArray(pubCards) ? pubCards.filter((e) => e && e.publicacion_id) : [];
  if (cards.length === 0) return resto;
  const unida = [
    ...(Array.isArray(resto.doc_cards) ? resto.doc_cards : []),
    ...cards.map((e) => ({ ...e, id: `${PREFIJO_CARD}${e.publicacion_id}`, doc_id: e.id })),
  ];
  const doc_cards = unida
    .map((e, i) => [e, i])
    .sort(([a, ia], [b, ib]) => {
      const ka = llaveDeOrden(a);
      const kb = llaveDeOrden(b);
      if (ka !== kb) return ka < kb ? 1 : -1;
      return ia - ib;
    })
    .map(([e]) => e);
  return { ...resto, doc_cards };
}

export async function fetchAnalyticsOverview(userId, isGuest) {
  if (analyticsRunsLocally() && isExplicitlyOffline()) {
    try {
      const sources = await loadSources();
      const fromWarehouse = await sources.analyticsOverviewFromWarehouse(userId, isGuest);
      if (fromWarehouse) return marcarOverviewDeBodega(fromWarehouse);
    } catch (e) { /* sin bodega utilizable → camino normal de abajo */ }
  }
  if (IS_APP_BUILD && isExplicitlyOffline()) throw offlineShapedError();
  try {
    const res = await api.get('/analytics/overview');
    // t7-1c (#480): las cards de `pub_cards`, unidas a `doc_cards` (web) o fuera
    // (app), y la llave retirada; sin ella, `datos` ES `res.data`. Viaja también a
    // los totales de la bodega: su compositor devuelve una copia de lo que recibe,
    // `doc_cards` incluido, así que tampoco ahí reaparece `pub_cards`.
    const datos = conLasCards(res.data);
    // ── APP-1 B5 (firma #402) — EL NÚMERO DEJA DE RETROCEDER ─────────────────
    // Con señal esta pantalla preguntaba SÓLO al servidor, y el servidor no sabe
    // todavía lo que este teléfono acaba de cerrar: por eso sin red el número iba
    // por encima y al volver la red BAJABA. Aquí se compone con el par (foto,
    // snapshot) de la bodega tomando el MÁXIMO componente a componente — el
    // razonamiento entero, y por qué sumar sería doble conteo, vive en
    // `brain/analyticsReads.js::overviewTotalsMostrados`.
    // Sólo donde hay bodega (la app): en la web `analyticsRunsLocally()` es falso y
    // esta rama es la de siempre, byte a byte.
    if (analyticsRunsLocally()) {
      try {
        const sources = await loadSources();
        const reads = await loadReads();
        const delPar = await sources.analyticsTotalsFromWarehouse(userId);
        if (delPar) return { ...datos, ...reads.overviewTotalsMostrados(datos, delPar) };
      } catch (e) { /* sin bodega utilizable → lo que dijo el servidor */ }
    }
    return datos;
  } catch (err) {
    if (analyticsRunsLocally() && isNetworkShapedError(err)) {
      const sources = await loadSources();
      const fromWarehouse = await sources.analyticsOverviewFromWarehouse(userId, isGuest);
      if (fromWarehouse) return marcarOverviewDeBodega(fromWarehouse);
    }
    throw err;
  }
}

// ── F6.8 G-3 — UNA VEZ POR ENTREGA: DE DÓNDE VINO EL DETALLE ────────────────
//
// EL DOBLE CONTEO (CONFLICTO 2, nacido con el corte de la T3). El bloque de
// examen del detalle se armaba en DOS puntos que no se conocían entre sí:
//
//   · SIN SEÑAL la base la arma EL MOTOR, y el motor YA fusiona las entregas
//     locales que la foto no trae (`mergeSessionRows`, brain/analyticsReads.js);
//   · encima, al pintar, la pantalla le fusionaba `examDeliveriesFromWarehouse`
//     (RPW T5), cuyos dos candados miran la FOTO y el DIARIO — ninguno de los
//     dos sabe lo que el motor acaba de incluir.
//
// Resultado: una entrega local NO ACUSADA entraba DOS veces. El invariante que
// manda es uno solo: cada entrega aparece EXACTAMENTE UNA vez en el bloque —
// con señal y sin ella, acusada o pendiente, en foto o fuera de ella.
//
// POR QUÉ PROCEDENCIA Y NO DEDUP POR IDENTIDAD DENTRO DE LA FUSIÓN. Deduplicar
// allá sería más robusto (cubriría los dos caminos), pero EL BLOQUE BASE NO
// TRAE IDENTIDADES: `build_exam_analytics` emite `{date, score, correct, total,
// time_limit, time_taken_minutes}` y ni un `session_id` (engine_core.py:848-853),
// y el renglón local lo calca campo por campo (screenSources.js:392-404). Sin id
// solo quedaría emparejar por HUELLA (fecha+score+total), que es adivinar, no
// reconocer. La señal mínima que sí existe es DE DÓNDE VINO LA RESPUESTA, y el
// fetcher es el único que lo sabe.
//
// Y NO ENSANCHA NINGÚN SHAPE: la marca vive en un WeakSet de este módulo, no en
// un campo del objeto. Las dos respuestas —la viva y la de bodega— siguen
// saliendo del fetcher POR IDENTIDAD, tal cual su fuente las devolvió.
const DETALLE_DESDE_BODEGA = new WeakSet();

function marcarDeBodega(detalle) {
  if (detalle && typeof detalle === 'object') DETALLE_DESDE_BODEGA.add(detalle);
  return detalle;
}

// El DETALLE por documento es fetcher DIRECTO a propósito (sin screenDataCache):
// es una acción explícita del usuario y siempre debe traer la verdad del
// momento. Esa infraestructura NO se unifica con la del overview — solo gana las
// mismas dos puertas. Su estado residual sigue siendo `setDetailError`, que el
// render traduce al aviso honesto o al mensaje genérico.
// t7-1c (#480): `publicacionId` (opcional) pide el detalle de UNA card: el mismo
// endpoint con su documento en la ruta y `?publicacion_id=`. Sin él, la llamada de
// siempre, byte a byte. t7-3 (A6): las dos puertas de la bodega (la app sin red) leen la
// card por su id LOCAL, `pub:<publicacion_id>`, que es donde vive su paquete y su progreso.
export async function fetchAnalyticsDocument(userId, isGuest, docId, publicacionId) {
  const idEnBodega = publicacionId ? `${PREFIJO_CARD}${publicacionId}` : docId;
  if (analyticsRunsLocally() && isExplicitlyOffline()) {
    try {
      const sources = await loadSources();
      const fromWarehouse = await sources.analyticsDocumentFromWarehouse(userId, isGuest, idEnBodega);
      if (fromWarehouse) return marcarDeBodega(fromWarehouse);
    } catch (e) { /* sin bodega utilizable → camino normal de abajo */ }
  }
  // Mismo atajo que el overview: sin señal el detalle tampoco tiene de dónde
  // salir, y su estado honesto es el MISMO aviso existente. Dejar que la
  // petición agote su techo solo añadiría espera antes de decir lo mismo.
  if (IS_APP_BUILD && isExplicitlyOffline()) throw offlineShapedError();
  try {
    const res = await api.get(publicacionId
      ? `/analytics/document/${docId}?publicacion_id=${encodeURIComponent(publicacionId)}`
      : `/analytics/document/${docId}`);
    return res.data;
  } catch (err) {
    if (analyticsRunsLocally() && isNetworkShapedError(err)) {
      const sources = await loadSources();
      const fromWarehouse = await sources.analyticsDocumentFromWarehouse(userId, isGuest, idEnBodega);
      if (fromWarehouse) return marcarDeBodega(fromWarehouse);
    }
    throw err;
  }
}

// ── TANDA 15 (#192) — LA ÚNICA RESPUESTA QUE SIGNIFICA "ESTE YA NO EXISTE" ──
//
// El backend nombra cada falla con un `error_code` estable
// (`UserFacingError`, backend/user_errors.py) y lo serializa como campo HERMANO
// del `detail`. El interceptor de `lib/api` traduce el `detail` —y sólo el
// `detail`—, así que este campo llega intacto al componente.
//
// SE EXIGEN LAS DOS SEÑAS a propósito. Con el estado solo, cualquier 404 futuro
// de esa ruta cerraría el detalle sin decir nada; con el código solo, un sobre
// ajeno que reusara el nombre haría lo mismo. Juntas, esto es exactamente el
// documento que el servidor ya no tiene.
function esDocumentoBorrado(err) {
  return err?.response?.status === 404
    && err?.response?.data?.error_code === 'doc_not_found';
}

export default function Analytics() {
  // PAGER-1 — ¿el usuario está viendo ESTA raíz? Sin proveedor arriba: `true`,
  // que es la verdad de hoy (el router monta una sola raíz). En PAGER-2 el
  // proveedor lo pone el pager por encima. Ver lib/pager/activacion.js.
  const activa = useRaizActiva();
  const { t, i18n } = useTranslation('study');
  const navigate = useNavigate();
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docAnalytics, setDocAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('adaptive');

  // COS-19b / COS-19c — LOS DOS SENTIDOS ATERRIZAN HASTA ARRIBA.
  //
  // Por qué el chasis ni se entera: aquí lista↔detalle NO es un cambio de ruta,
  // es un cambio de ESTADO (`selectedDoc`) dentro de la misma pantalla montada.
  // El scroll de la app autenticada vive en el `<main>` del chasis (App.js:119-124)
  // —el MISMO nodo para todas las pantallas protegidas—, así que su `scrollTop`
  // sobrevive intacto al cambio de vista: recorrías los temas del detalle,
  // tapeabas el regreso, y la lista se pintaba con el scroll heredado.
  //
  // Mismo mecanismo que COS-19 (SettingsPage.js:95-98), escrito donde este caso
  // lo necesita — esta pantalla no se desmonta, así que un efecto de MONTAJE no
  // sirve. COS-19b cubrió los DOS caminos de vuelta a la lista (la píldora del
  // detalle y el botón del detalle caído); COS-19c cerró la simetría por el otro
  // lado, en la ENTRADA al detalle (la tarjeta de la lista): si venías
  // scrolleado buscando un documento del fondo, su detalle abría por la mitad.
  //
  // DÓNDE SE DISPARA, HOY: en el efecto DE LAYOUT de más abajo, y en los DOS
  // sentidos. R-6 se llevó allí la entrada (el 0 caía sobre la lista) y la Tanda
  // G se llevó la vuelta (caía sobre el detalle). Ya no queda ni una llamada en
  // un `onClick`: un solo helper, un solo disparador. Sin scrollport (arneses que
  // montan la página suelta) es no-op.
  //
  // PAGER-2 — A QUÉ SCROLLER LE ESCRIBE. Todo el párrafo de arriba sobre el
  // `<main>` compartido describe la app ANTES del carril, y sigue siendo la
  // verdad en web. En la app las cuatro raíces viven en el carril y CADA PANE es
  // su propio scroller: el `<main>` ya no scrollea para ellas, así que escribirle
  // sería un no-op silencioso y el detalle abriría a media lista otra vez.
  // `useScrollportRaiz()` devuelve el scroller de ESTA pane cuando hay carril y
  // el `<main data-testid="content-area">` de siempre cuando no lo hay (web,
  // montaje suelto, arnés). Lo que NO cambia: sigue siendo UNA sola escritura,
  // desde el mismo efecto de layout, con las mismas dos guardas de fuente
  // encima (R-6 y Tanda G).
  const scrollportDeLaRaiz = useScrollportRaiz();
  const aterrizarAlTope = () => {
    const scrollport = scrollportDeLaRaiz();
    if (scrollport) scrollport.scrollTop = 0;
  };

  // F6.8 T3 — la identidad sube ANTES del fetcher: la guardia de dueño de la
  // bodega exige que quien pide el dato sea su dueño, y el ensamblador necesita
  // además si es invitado (el núcleo deriva de ahí lo mismo que el servidor).
  // La lee `useAuth`, que esta pantalla ya consumía más abajo (RPW T5).
  const { user } = useAuth();
  const tutorDeAnaliticas = useTutorDeLaSesion();
  const userId = user?.id;
  const isGuest = user?.is_guest;

  // Identidad nueva por render sin costo: useScreenData guarda el fetcher en un
  // ref, así que cambiarlo NO re-dispara fetches (mismo patrón que StudyCenter).
  // PAGER-1: RED (`GET /analytics/overview`, carril `study_user` 150/h).
  // `enabled` es la puerta que `useScreenData` ya tenía.
  const { data: overviewData, loading: overviewLoading, error: overviewError, refetch: refetchOverview } = useScreenData('analytics-overview', () => fetchAnalyticsOverview(userId, isGuest), { enabled: activa });
  const overview = overviewData || null;
  // t7-1c (#480): el detalle de una card necesita su documento y su
  // `publicacion_id`, que viven en su entrada de la lista (ver `conLasCards`). Va en
  // un ref y no en las deps del fetcher del detalle: si el fetcher cambiara de
  // identidad cada vez que llega el overview, su efecto volvería a pedir el detalle.
  const overviewVigenteRef = useRef(overview);
  overviewVigenteRef.current = overview;
  // F6.7b (fase 2) — el aviso "sin conexión" era terminal: quedaba en pantalla
  // aunque la señal volviera, hasta navegar. Con el dictamen del vigía se
  // re-pide sola. Reacción a un hecho, no un reloj. Sigue haciendo falta con el
  // corte de F6.8 T3: la bodega puede no tener nada que decir (aviso residual) y
  // al volver la señal el servidor sí.
  // PAGER-1: reacción a NET.UP → RED. Sólo la raíz activa.
  const net = useNetworkStatus();
  useEffect(() => {
    if (!activa) return;
    // ── APP-1 B5 (firma #402) — AL VOLVER LA RED SE RE-PIDE SIEMPRE ──────────
    // La exigencia de `overviewError` dejaba fuera el caso que más importa: un
    // overview servido de BODEGA no es un error, así que al volver la señal nadie
    // re-preguntaba y la pantalla se quedaba con los números de cuando no había
    // red. Es justo el recorrido del modo avión que Carlos midió. Re-pedir sin
    // error no es un reloj: sigue siendo la reacción a UN hecho (NET.UP), tiene
    // fin, y el handler que paga ya no hace 34 idas a Mongo.
    //   VERBATIM ANTES:
    //       if (!net.isOnline || !overviewError) return;
    if (!net.isOnline) return;
    refetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [net.isOnline, activa]);


  // ── TANDA 15 (#189b) — EL AVISO DE DOCS TAMBIÉN SE ESCUCHA AQUÍ ───────────
  //
  // POR QUÉ NO BASTA CON INVALIDAR (la pieza hermana, #189a). Tirar la llave
  // garantiza que la PRÓXIMA entrada vuelva a preguntar; no repinta una
  // pantalla ya montada. Y con el carril las cuatro raíces están montadas a la
  // vez, así que la que el usuario está mirando se quedaría con la lista vieja
  // —tarjeta de un documento ya borrado incluida— hasta salir y volver. Las dos
  // piezas son la división de siempre: el cache decide QUÉ llaves caen, la
  // pantalla montada decide CUÁNDO repintar.
  //
  // EL MOLDE ES EL DEL PANEL (`Dashboard.js`, el efecto de foco/docs): gate de
  // identidad, oído con su limpieza, y el re-pedido por el camino que YA EXISTE
  // —el mismo `refetchOverview` de los dos botones "Reintentar" y del efecto de
  // NET.UP de arriba—. Cero rutas de fetch nuevas, cero estados de red nuevos,
  // cero relojes. Su identidad es estable (`useCallback` sobre la llave).
  //
  // SÓLO ESE AVISO. `local-material-ready` (material que terminó de bajar a la
  // bodega) y `doc-uploaded` (documento recién subido) no cambian un solo
  // número de esta pantalla: ni sesiones, ni readiness, ni totales. El Panel
  // los escucha porque pinta la lista de archivos; aquí serían una petición a
  // cambio de nada.
  //
  // ── TANDA 15e (#193) — Y EL OÍDO SE REGISTRA AUNQUE LA RAÍZ DUERMA ────────
  //
  // POR QUÉ EL GATE DE ACTIVACIÓN NO PODÍA IR EN EL REGISTRO. La raíz que borra
  // es SIEMPRE OTRA: el bote vive en el Panel. O sea que el aviso llega, sin
  // excepción, con Analíticas dormida — y con el oído colgado de la activación
  // no había oído: nadie escuchaba el único evento que importa. Al volver
  // tampoco se pedía, porque `useScreenData` congela su semilla al MONTAR y
  // corta el fetch con `seedFresh` aunque la llave ya esté invalidada. Medido
  // en la tanda anterior: cero peticiones y la tarjeta del documento borrado
  // seguía pintada.
  //
  // EL GATE NO DESAPARECE, SE MUDA AL HANDLER, y ahí sigue siendo la doctrina
  // del carril intacta: dormida NO pide, NO escribe caché y NO estrena un solo
  // reloj — sólo deja una MARCA en un ref (ni estado, ni render, ni red). Es la
  // diferencia entre "no escuchar" y "escuchar y anotar", y sólo la segunda
  // sobrevive a que el evento llegue mientras duermes.
  const avisoPendienteRef = useRef(false);
  useEffect(() => {
    if (!user) return undefined;
    const alCambiarLosDocs = () => {
      if (activa) { refetchOverview(); return; }
      avisoPendienteRef.current = true;
    };
    window.addEventListener('studybuddy:docs-changed', alCambiarLosDocs);
    return () => {
      window.removeEventListener('studybuddy:docs-changed', alCambiarLosDocs);
    };
  }, [user, refetchOverview, activa]);

  // ── APP-1 B5 (firma #402) — EL OÍDO DEL CIERRE DE SESIÓN ────────────────────
  //
  // El hallazgo de Carlos: «una vez que pintó un número, no vuelve a preguntar
  // hasta que sales de la pestaña y regresas. Si te quedas mirándola, el número no
  // se corrige nunca». Analíticas no tenía ni reloj ni oído para esto.
  //
  // EL MOLDE ES EL DE `docs-changed`, el de aquí arriba, y con su misma doctrina:
  // gate en el HANDLER y no en el registro (la sesión se cierra desde OTRA raíz,
  // así que el evento llega con Analíticas dormida), marca en un ref cuando duerme,
  // y el re-pedido por el camino que YA existe. Cero rutas nuevas, cero relojes.
  //
  // EL EVENTO YA EXISTE y ya lo emiten los dos runtimes y las dos ramas web
  // (`lib/screenEvents.js`): esta tanda no estrena ninguno, sólo lo escucha.
  useEffect(() => {
    if (!user) return undefined;
    const alCerrarSesion = () => {
      if (activa) { refetchOverview(); return; }
      avisoPendienteRef.current = true;
    };
    window.addEventListener('studybuddy:session-committed', alCerrarSesion);
    return () => {
      window.removeEventListener('studybuddy:session-committed', alCerrarSesion);
    };
  }, [user, refetchOverview, activa]);

  // LA DESCARGA DE LA MARCA, al activarse. `refetchOverview` NO consulta
  // `seedFresh` —va derecho a `fetchScreenData`—, que es justo lo que hace falta
  // para despertar a una pane que montó con copia fresca.
  //
  // Y LA DOBLE RUTA DE LA ACTIVACIÓN NO SE SUPRIME A MANO: cuando la semilla no
  // era fresca, `useScreenData` también pide al activarse, y las dos llamadas se
  // colapsan solas en el dedupe por llave de `fetchScreenData` (su efecto está
  // declarado más arriba, así que la promesa ya está en vuelo cuando esta corre).
  // Inventar una supresión aquí sería una segunda implementación de ese dedupe.
  //
  // LA MARCA SE GASTA: se limpia ANTES de pedir, así que una activación no
  // arrastra la anterior.
  useEffect(() => {
    if (!activa || !user) return;
    if (!avisoPendienteRef.current) return;
    avisoPendienteRef.current = false;
    refetchOverview();
  }, [activa, user, refetchOverview]);

  // ── RPW T5 (P3) — LO QUE EL CEL YA SABE, EL CEL LO MUESTRA ────────────────
  //
  // EL HALLAZGO (F1 del smoke): una entrega hecha en el cel no aparecía en la
  // PRIMERA visita a Analytics — aparecía al salir y volver. No era cache: lo que
  // cambiaba en medio era el SERVIDOR. El acta se guarda local al instante, pero
  // el cartero la sube con debounce, así que la primera visita pedía el detalle
  // antes de que el servidor la tuviera.
  //
  // Las entregas locales que el servidor aún no refleja se leen UNA vez al montar
  // —en paralelo con el overview, sin bloquear nada— y se fusionan al pintar. En
  // web `examDeliveriesFromWarehouse` devuelve `[]` sin tocar disco y la fusión es
  // la identidad: cero cambios. Es LECTURA para pintar: no escribe, no marca
  // actas, no despierta al cartero.
  const [localDeliveries, setLocalDeliveries] = useState([]);
  useEffect(() => {
    if (!IS_APP_BUILD || !userId) return undefined;
    let vivo = true;
    examDeliveriesFromWarehouse(userId)
      .then((entregas) => { if (vivo) setLocalDeliveries(entregas); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [userId]);

  const [detailLoading, setDetailLoading] = useState(false);
  // F6.4b: el detalle guarda su error en estado (no toast) para poder pintar
  // el aviso honesto. F6.8 T3 lo conserva como estado RESIDUAL: ahora se llega
  // aquí solo cuando la bodega tampoco tuvo nada que servir.
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    // F6.4b: un fallo con forma-de-red pinta el estado honesto de abajo — el
    // toast queda solo para errores reales del endpoint (ahora con i18n).
    //
    // F6.8 R-4 (firma de Carlos) — UNA SOLA VOZ POR ERROR. Hasta aquí el 4xx
    // sonaba SIEMPRE, y desde EH-2 eso significaba toast + tarjeta del estado 3
    // diciendo el mismo hecho con dos frases distintas (C-R-1 / H-R-3 del remate
    // EH). Sin overview no hay nada que enseñar y SIEMPRE pinta una tarjeta —el
    // estado 1 si no hubo señal, el estado 3 si la hubo— y esa tarjeta trae su
    // botón: ELLA es el mensaje, y el toast se calla. El toast sobrevive donde
    // ninguna tarjeta pinta: con el overview YA en pantalla (una recarga que
    // falló y conserva el dato leído), que sin él sería un fallo mudo.
    if (overviewError && !isNetworkShapedError(overviewError) && overview) {
      toast.error(t('analytics.load_failed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewError]);

  /* ── TANDA 15 (#192) — LA SALIDA DEL DETALLE DE UN DOCUMENTO MUERTO ───────
     Un cierre INVOLUNTARIO, no un gesto del usuario: por eso no reusa ninguno
     de los dos regresos de la Tanda G y limpia LOS DOS estados de detalle. Esas
     dos funciones nacen cada una en su vista y saben cuál está pintada
     (`volverALaLista` la buena, `volverDelDetalleCaido` la caída); esto se
     dispara desde fuera, sin saber en cuál estabas, y dejar el otro estado en
     pie sería dejar basura para el siguiente documento que abras.
     Identidad estable (los setters de estado lo son), para que entre en las
     deps del fetcher sin re-disparar su efecto en cada render. */
  const lecturaVigenteRef = useRef(null);
  const desalojarDetalle = useCallback(() => {
    setSelectedDoc(null);
    setDocAnalytics(null);
    setDetailError(null);
    setActiveTab('adaptive');
  }, []);

  // F6.8 T3: el cuerpo del fetch vive arriba (`fetchAnalyticsDocument`); aquí
  // queda SOLO el manejo de estado de la pantalla. Las deps ganan la identidad
  // —primitivas, y el guard de ruta ya exige un `user` resuelto para montar esta
  // pantalla, así que en producción no cambian a media vida.
  const fetchDocAnalytics = useCallback(async (docId) => {
    // TANDA 15 (#192) — CUÁL ES LA LECTURA VIGENTE. Un 404 que llega tarde habla
    // de SU documento, no del que está seleccionado ahora, y sin esta marca
    // cancelaba la selección viva —pantalla en la lista, sin decir nada, con un
    // documento perfectamente sano—. Es un ref, no estado: no re-renderiza ni
    // añade red.
    //
    // TANDA 16 (#197) — CAMBIÓ EL CAMINO QUE LO PROVOCA, NO LA NECESIDAD. Cuando
    // esto se escribió, con el detalle en vuelo la pantalla caía A LA LISTA y el
    // usuario podía tocar otra tarjeta ahí mismo; ahora ese hueco lo llena la
    // silueta del detalle, así que desde la lista ya no se alcanza. La carrera
    // sigue viva por el otro lado, que es igual de real: con un detalle YA
    // pintado, la re-lectura del despertar deja su petición en el aire, el
    // regreso a la lista no la cancela, y la tarjeta que se toque después es la
    // que el 404 rezagado se llevaría por delante. La marca cuesta un ref.
    lecturaVigenteRef.current = docId;
    // t7-1c (#480): una card ("pub:<id>") se pide por el documento y la
    // `publicacion_id` de su entrada; un documento, la llamada de siempre. La marca
    // de la lectura vigente sigue siendo el id de la selección, así que dos cards del
    // mismo documento son dos lecturas distintas para el cerrojo 1. Una card que ya
    // no está en la lista es un fantasma: se desaloja como lo haría el cerrojo 2.
    const card = esIdDeCard(docId)
      ? ((overviewVigenteRef.current && overviewVigenteRef.current.doc_cards) || [])
        .find((e) => e && e.id === docId)
      : null;
    if (esIdDeCard(docId) && !card) { desalojarDetalle(); return; }
    setDetailLoading(true);
    setDetailError(null);
    try {
      setDocAnalytics(await (card
        // t7-3 (A6): la entrada de una card que salio de la bodega (sin red) no trae `doc_id`
        // ni `publicacion_id`: se leen de su id local.
        ? fetchAnalyticsDocument(userId, isGuest, card.doc_id || docId,
          card.publicacion_id || docId.slice(PREFIJO_CARD.length))
        : fetchAnalyticsDocument(userId, isGuest, docId)));
    } catch (err) {
      // ── TANDA 15 (#192), CERROJO 1 — EL DOCUMENTO YA NO EXISTE ───────────
      //
      // POR QUÉ ESTA RAMA NO ES UN LUJO. Esta lectura SE REPITE: su efecto
      // lleva `activa` en las deps, así que dormirse y volver la vuelve a
      // disparar. Si en esa ausencia borraste el documento en el Panel —la raíz
      // de al lado—, aquí llega el 404… y el render NO se entera: mira
      // `detailError` sólo cuando no hay `docAnalytics`, y en este camino lo
      // hay, del documento que acaba de morir. El detalle sobrevivía a su
      // propia muerte, con su título y su botón de estudiar.
      //
      // LAS DOS SEÑAS, Y LAS DOS HACEN FALTA: `404` + `error_code`. El sobre
      // del backend nombra cada falla con un código estable (`user_errors.py`),
      // y el interceptor de `lib/api` traduce el `detail` pero jamás toca ese
      // campo. Un 404 anónimo, un 5xx o un fallo de red NO son "documento
      // borrado" y conservan intacto el estado honesto de siempre.
      if (esDocumentoBorrado(err) && lecturaVigenteRef.current === docId) {
        desalojarDetalle(); return;
      }
      // F6.4b: sin toast — el error queda en estado y el render decide entre
      // el aviso honesto offline y el mensaje genérico (ambos i18n).
      setDetailError(err);
    } finally {
      setDetailLoading(false);
    }
  }, [userId, isGuest, desalojarDetalle]);

  // PAGER-1: `GET /analytics/document/{id}` — RED, carril `study_user`. Hoy ya
  // está condicionado a `selectedDoc` (que arranca en null, así que no corre al
  // montar); el gate se añade igual, para que el efecto declare de qué momento
  // depende en vez de depender de que nadie pueda tocar una tarjeta invisible.
  useEffect(() => {
    if (!activa) return;
    if (selectedDoc) {
      setActiveTab('adaptive');
      fetchDocAnalytics(selectedDoc);
    }
  }, [selectedDoc, fetchDocAnalytics, activa]);

  // ── TANDA 15 (#192), CERROJO 2 — EL DESALOJO POR AUSENCIA ─────────────────
  //
  // LO QUE CUBRE que el cerrojo 1 no puede: el detalle abierto en una raíz
  // DORMIDA cuyo documento se borra en el Panel. Al volver, la activación
  // re-pide el overview, éste llega sin la tarjeta… y hasta aquí el detalle
  // seguía en pie, porque nadie comparaba las dos cosas. La lista es la verdad
  // de qué documentos existen; un detalle que no está en ella es un fantasma.
  //
  // Y ES ADEMÁS EL QUE CIERRA EL CAMINO SIN SEÑAL, sin tocarlo: ahí el overview
  // sale de LA MISMA bodega que la escoba de #190 acaba de barrer. El camino de
  // error del detalle no sirve para eso —`analyticsDocumentFromWarehouse`
  // devuelve `null` igual para "documento borrado" que para "bodega vacía",
  // "instalación nueva" o "dueño ajeno", y esos tres deben seguir pintando el
  // aviso residual con su regreso—; esto sí, porque mira una LISTA, no un
  // `null`.
  //
  // LAS TRES GUARDAS, y ninguna sobra:
  //   · sin `selectedDoc` no hay nada que desalojar;
  //   · SIN OVERVIEW NO SE DECIDE. Un overview ausente (la primera carga, o una
  //     recarga que falló) no es "el documento no existe": es "todavía no sé".
  //     Cerrar ahí sería tirar el detalle cada vez que la red tosa.
  //   · y `doc_cards` tiene que ser una lista de verdad antes de buscar en ella.
  //
  // NO PIDE NADA Y NO ESTRENA UN SOLO RELOJ: es estado local mirando estado
  // local. El aterrizaje al tope lo hace el efecto de layout de más abajo, con
  // su propio gate de activación, exactamente igual que los dos regresos.
  useEffect(() => {
    if (!selectedDoc) return;
    if (!overview || !Array.isArray(overview.doc_cards)) return;
    // …y NO SOBRE UNA LISTA PARCIAL: la de la bodega sólo conoce lo descargado a
    // este aparato (ver el letrero de `OVERVIEW_DESDE_BODEGA`, arriba).
    if (OVERVIEW_DESDE_BODEGA.has(overview)) return;
    if (overview.doc_cards.some((card) => card && card.id === selectedDoc)) return;
    desalojarDetalle();
  }, [overview, selectedDoc, desalojarDetalle]);

  /* ── R-6 — EL ATERRIZAJE AL TOPE, AHORA INVISIBLE ─────────────────────────
     (COS-19c se conserva entero: el detalle sigue abriendo desde arriba.)

     EL BRINCO QUE SE VEÍA, Y POR QUÉ. `aterrizarAlTope()` vivía en el onClick de
     la tarjeta, o sea corría ANTES de que hubiera un detalle: en ese instante lo
     único montado era la LISTA, y el detalle no llega hasta que su fetch vuelve
     (el `useEffect` de arriba ni siquiera ha corrido todavía — es pasivo). El
     orden real, medido en el arnés (Analytics.remateAterrizaje.test.js lo fija,
     fotografiando el DOM dentro del setter de `scrollTop`): se escribe
     el 0 → el DOM sigue teniendo `analytics-page` y NO tiene
     `analytics-document-detail` → el navegador pinta ESE frame. Resultado: la
     lista salta hasta arriba a la vista del usuario, se queda ahí lo que tarde
     la lectura, y sólo después la reemplaza el detalle. Dos movimientos donde
     debía haber uno — el "parpadeo/brinco" del re-smoke.

     TANDA 16 (#197) — LO QUE SE VE EN ESA ESPERA YA NO ES LA LISTA, es la
     silueta del detalle (la rama nueva de más abajo). Esto NO afecta al arreglo
     ni a su medición, y conviene entender por qué: el único disparador del
     aterrizaje es `detalleEnPantalla`, que exige datos o error, y durante la
     espera vale falso. O sea la silueta no provoca ni una escritura de más, y la
     foto se sigue tomando en el mismo instante de siempre. El párrafo de arriba
     se conserva porque describe el DEFECTO histórico —el que justifica que el
     helper viva en un efecto de layout y no en el onClick—, y ese sigue siendo
     el motivo por el que no se puede devolver a su sitio viejo.

     LA `key` DE COS-32 NO PARTICIPA (la sospecha del encargo, comprobada y
     descartada): el remonte ocurre en el MISMO commit —React quita un nodo y
     mete otro sin pintar en medio— y un nodo recién insertado no transiciona.
     Lo que se veía no era una animación; era un scroll aplicado a la vista
     equivocada. Por eso la `key` se queda tal cual: quitarla resucitaría COS-32.

     EL ARREGLO. El mismo helper, movido al instante correcto: cuando el detalle
     YA está en el DOM. `useLayoutEffect` corre después de las mutaciones y ANTES
     del paint, así que el primer frame del detalle nace en el tope y la lista
     nunca se mueve bajo el dedo. Un `useEffect` normal no sirve: pinta primero y
     corrige después, que es el brinco otra vez con otro disfraz.

     Cubre las DOS vistas de detalle —la buena y la del detalle caído— porque el
     onClick reseteaba para ambas.

     ── TANDA G (Encargo 2) — Y AHORA TAMBIÉN EL CAMINO DE VUELTA ─────────────
     R-6 dejó anotado en sus HUECOS un "gemelo simétrico": los DOS regresos de
     COS-19b seguían llamando a `aterrizarAlTope()` DENTRO del onClick, o sea
     ANTES de que React quitara el detalle. En ese instante lo montado es el
     DETALLE, así que el 0 caía sobre él: el detalle brincaba al tope a la vista
     del usuario y sólo después lo reemplazaba la lista. Exactamente el mismo bug
     que R-6 midió en la entrada, con la vista contraria.

     El arreglo es el mismo patrón, aplicado al otro borde de la bandera: el
     helper se va de los dos handlers y lo dispara ESTE efecto de layout cuando
     el detalle ya SALIÓ del DOM y antes del primer paint de la lista. Un
     `useEffect` normal no sirve —pintaría la lista en el scroll heredado y la
     corregiría después, que es el brinco otra vez— y por eso la guarda de fuente
     de R-6 sigue siendo load-bearing.

     `detalleAntesRef` es lo que distingue "volví del detalle" de "acabo de
     montar la pantalla": al montar, la bandera nace abajo y el cuerpo no toca el
     scroll de nadie — entrar a Analíticas no reposiciona la vista, igual que
     hoy. Con esto el gemelo muere.

     ── PAGER-1 — ESCRITURA AL CHASIS COMPARTIDO ──────────────────────────────
     `aterrizarAlTope()` escribe el `scrollTop` del `<main data-testid=
     "content-area">`, que es UN SOLO NODO para las cuatro raíces (App.js) — de
     los peores mentirosos del censo: una raíz dormida que lo tocara movería la
     pantalla que el usuario SÍ está mirando. Por eso las DOS escrituras cuelgan
     de la activación.

     EL GATE VA EN LAS DOS LLAMADAS Y NO ARRIBA DEL EFECTO, y `activa` NO ENTRA
     EN LAS DEPS. No es una concesión a las dos guardas de fuente que fijan la
     forma de este efecto (R-6 y Tanda G, que protegen que sea DE LAYOUT y que
     los dos bordes de la bandera vivan aquí dentro): es que meterlo en las deps
     sería un CAMBIO DE COMPORTAMIENTO. Con `activa` en las deps, ACTIVARSE
     re-correría el efecto y aterrizaría el scroll del chasis sin que la vista
     hubiera cambiado — y la regla escrita arriba dice justo lo contrario:
     ENTRAR a Analíticas no reposiciona la vista de nadie. Activarse es entrar.
     Tampoco hay clausura vieja que temer (relectura de deps, remate F): el
     único disparador es `detalleEnPantalla`, y cada vez que cambia el efecto
     corre con el `activa` de ESE render. Un `activa` que cambia solo, sin que
     la vista cambie, tiene que ser exactamente un no-op.

     La contabilidad de `detalleAntesRef` queda FUERA del gate a propósito: es
     memoria de "hubo detalle", no una escritura a nada compartido — y de todos
     modos una raíz dormida no puede abrir un detalle (nadie puede tocar una
     tarjeta invisible, y su fetch está gateado). */
  const detalleEnPantalla = Boolean(selectedDoc && (docAnalytics || detailError));
  const detalleAntesRef = useRef(false);
  useLayoutEffect(() => {
    if (detalleEnPantalla) { detalleAntesRef.current = true; if (activa) aterrizarAlTope(); return; }
    if (!detalleAntesRef.current) return;   // montaje: no había detalle del cual volver
    detalleAntesRef.current = false;
    if (activa) aterrizarAlTope();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalleEnPantalla]);

  /* ── TANDA G (Encargo 2) — LOS DOS REGRESOS, CON NOMBRE ────────────────────
     Eran dos lambdas en línea dentro del `onClick` de sus botones. El cuerpo es
     EL MISMO (mismos setters, mismo orden) menos la llamada a `aterrizarAlTope`,
     que se fue al efecto de arriba; lo único que cambia es que ahora tienen
     nombre, y por eso el gesto de borde puede llamar exactamente a la función
     del botón en vez de navegar por su cuenta. Sabotear una rompe las dos
     puertas, que es lo que fija el rojo de mutación.

     SIGUEN SIENDO DOS y no una: cada vista de detalle limpia SU estado
     (`docAnalytics` la buena, `detailError` la caída). Fundirlas obligaría a
     limpiar los dos siempre, que es un cambio de comportamiento que este encargo
     no pide. */
  const volverALaLista = () => {
    setSelectedDoc(null);
    setDocAnalytics(null);
    setActiveTab('adaptive');
  };
  const volverDelDetalleCaido = () => {
    setSelectedDoc(null);
    setDetailError(null);
    setActiveTab('adaptive');
  };

  /* ── TANDA G (Encargo 2) — EL GESTO DE BORDE, SÓLO EN EL DETALLE ───────────
     Firma de Carlos: el swipe de borde en el detalle de un documento regresa a
     la vista GENERAL de Analíticas, aterrizando hasta arriba. El commit es EL
     MISMO handler del botón de regresar (COS-19b), nunca un `navigate` paralelo:
     por eso el aterrizaje, el olvido del detalle y el reseteo de la pestaña
     quedan intactos por construcción — y el aterrizaje lo hace el efecto de
     layout de arriba, así que la vuelta del gesto llega al tope igual que la del
     botón, sin brinco.

     LAS DOS VISTAS DE DETALLE LO ESTRENAN, no sólo la buena: la del detalle
     caído es también un detalle, tiene el mismo botón de regreso y dejarla fuera
     sería el mismo HUECO que COS-H-b tuvo que volver a cerrar. Nombres distintos
     en el visor para poder contarlas por separado.

     LA VISTA GENERAL NO GANA GESTO (firmado): ahí no hay "atrás" interno, y el
     borde de esa pantalla no significa nada.

     SIN AVISO (firmado): salir del detalle no guarda nada, así que no hay nada
     que confirmar. El Encargo 1 no toca esta salida.

     CONVIVENCIA CON EL TAB SWIPE (COS-I, actualizada en PAGER-2): no hay
     conflicto que arbitrar. Antes eran dos candados —los props del swipe se
     esparcían sólo en `analytics-page`, que en el detalle ni se monta, Y su
     `enabled` se apagaba con `selectedDoc`—. Con el carril el swipe ya no vive
     en esta pantalla, así que queda el segundo: el detalle se REPORTA como capa
     encima (`useReportarCapa`, más abajo) y el carril no deja nacer el gesto. En
     el detalle la franja de 32 px sigue siendo del gesto de salida y el resto de
     la pantalla no es de nadie; en la lista, al revés. Un toque a 800 px del
     borde dentro del detalle no despierta ni a uno ni a otro. */
  const swipeDetalle = useEdgeSwipeBack({ screen: 'analytics-detail', onCommit: volverALaLista });
  const swipeDetalleCaido = useEdgeSwipeBack({ screen: 'analytics-detail-error', onCommit: volverDelDetalleCaido });

  /* COS-I — EL SWIPE ENTRE PESTAÑAS. Vecinas: Estudiar a la izquierda, Mejorar
     plan a la derecha.

     SOLO EN LA RAÍZ. El detalle no es una pestaña: ahí el gesto horizontal no
     significa "cambiar de sección". Quien quiera salir tiene su botón de regreso
     Y el gesto de borde de arriba (Tanda G), que no es esto: nace en los
     primeros 32 px y regresa a la lista, no a otra pestaña.

     PAGER-2 — DE DOS CANDADOS QUEDA UNO, Y ES EL QUE MANDABA. Antes esta
     pantalla montaba el gesto y lo apagaba por partida doble: los props se
     esparcían sólo en el contenedor de la lista (el detalle es OTRO `return` y
     nunca los veía) Y además con `enabled`. El primer candado era geometría del
     render y murió con el carril —el gesto ya no vive en esta pantalla, lo monta
     una vez `components/PagerRaices.js`—; el segundo es el que viaja, y es el
     mismo booleano de siempre: en esta pantalla la "capa encima" ES el detalle
     de un documento.

     Esta pantalla no tiene modales: el detalle es la única capa que declarar. */
  const capaEncima = !!selectedDoc;
  useReportarCapa(capaEncima);

  // pantallas-alumno (F): el encabezado del Panel se ve en todos los estados de la vista general, tambien
  // mientras carga, sin red y cuando no cargo (AN7, AN8, AN9: "los encabezados se quedan").
  const encabezado = (
    <EncabezadoDePagina
      titulo={<span data-testid="analytics-title">{t('analytics.title')}</span>}
      texto={t('analytics.subtitle')}
      acciones={tutorVisible(tutorDeAnaliticas, user) ? (
        // tutor-2 (D.2): abre el Tutor con la conversacion "Mis analiticas".
        <BotonTutor alTocar={() => navigate('/tutor?origen=analiticas')} testid="tutor-boton-analiticas" />
      ) : undefined}
    />
  );

  // ─── LOADING ─── (sin cache fresco → silueta de la pantalla, no spinner)
  if (overviewLoading && !overview && !docAnalytics) {
    return (
      <div className={PAGINA} data-testid="analytics-loading">
        {encabezado}
        <EsqueletoDeAnaliticas />
      </div>
    );
  }

  // ─── ESTADO RESIDUAL (F6.4b, resignificado en F6.8 T3) ─── El overview cayó
  // por red Y la bodega tampoco tuvo nada que servir: aviso claro con i18n, sin
  // toast y sin skeleton eterno. El copy dice lo que de verdad pasa — en este
  // dispositivo no hay datos de este apartado todavía.
  //
  // F6.8 EH-1 — EL VOLTEO. Hasta aquí esta rama solo miraba el ERROR RESIDUAL, y
  // por eso la pantalla no re-decidía al perder la señal: se re-renderizaba
  // (`useNetworkStatus` la tiene suscrita desde F6.7b) pero pintaba lo mismo,
  // byte a byte, hasta que alguien la mataba y volvía a entrar. Ahora el
  // VEREDICTO VIVO entra al árbol de decisión, que es lo único que hacía falta:
  // el dato ya está en memoria, no hay re-lectura, no hay oído nuevo. Es el
  // mismo molde de Ajustes (`SettingsPage.js:173, 329`).
  //
  // F6.8 EH-2 — LA FRONTERA. Antes ESTA misma rama se comía también los 5xx
  // (`isNetworkShapedError`) y les ponía encima un copy que decía "sin conexión"
  // a alguien que sí la tenía: el bug C-2 del lookup. La frontera ahora es una
  // sola línea y separa dos hechos distintos — no llegó al servidor (sin
  // conexión) vs. el servidor contestó mal (estado 3).
  const overviewSinRed = net.isOffline || (overviewError && isOfflineShapedError(overviewError));

  // ─── ESTADO 1 — SIN CONEXIÓN Y ESTE DISPOSITIVO NO GUARDA LA SECCIÓN ───
  //
  // F6.8 R-3 — LA WEB DICE SU VERDAD (cierre de C-EH-1). El copy firmado habla de
  // la bodega ("usa la app con internet al menos una vez: tus datos se guardan
  // aquí solos"), y eso es app-only: en la web no hay dónde guardarlos, así que
  // ahí ese texto es una promesa que el producto no puede cumplir. En web este
  // estado sólo se alcanza por un fallo que jamás llegó al servidor (el veredicto
  // es UNKNOWN para siempre — `networkVerdict.js:344`), o sea exactamente "revisa
  // tu internet". La app conserva su copy firmado; la selección es por build, en
  // el punto de render, y no toca ni la frontera ni el veredicto.
  // pantallas-alumno (F): el recuadro I2 del Panel con nube tachada, debajo del encabezado (AN7).
  if (!overview && overviewSinRed) {
    return (
      <div className={PAGINA} data-testid="analytics-sin-datos">
        {encabezado}
        <div data-testid="analytics-offline-notice">
          {IS_APP_BUILD ? (
            <RecuadroDeEstado tipo="sin_conexion" texto={t('analytics.offline_empty')} />
          ) : (
            <RecuadroDeEstado tipo="sin_conexion" texto={t('common:offline.no_connection.body')}
              accion={{ texto: t('common:offline.no_connection.cta'), onClick: () => refetchOverview(), icono: RotateCw, testid: 'analytics-offline-retry-btn' }} />
          )}
        </div>
      </div>
    );
  }

  // ─── ESTADO 3, CON SEÑAL, EL SERVIDOR FALLÓ ─── (AN8)
  // Antes esto era `return null`: una pantalla en blanco y un toast que se va
  // solo. El botón re-dispara LA MISMA carga de la pantalla: si sale, pinta; si
  // no, se queda aquí. Sin reintentos encadenados y sin spinner eterno,
  // `refetchOverview` no levanta `loading`, así que el estado no parpadea a esqueleto.
  if (!overview && overviewError) {
    return (
      <div className={PAGINA} data-testid="analytics-sin-datos">
        {encabezado}
        <div data-testid="analytics-load-error">
          <RecuadroDeEstado tipo="no_cargo" texto={t('analytics.load_error.body')}
            accion={{ texto: t('analytics.load_error.cta'), onClick: () => refetchOverview(), icono: RotateCw, testid: 'analytics-retry-btn' }} />
        </div>
      </div>
    );
  }

  // ─── F6.4b: detalle caído (red u otro) → estado honesto con regreso ───
  // pantallas-alumno (F, AN14 y AN15): el regreso es la MISMA pildora "All Documents" del detalle
  // bueno (hoy cambiaba de pildora a liga) y el recuadro es el I2 del Panel.
  if (selectedDoc && !docAnalytics && !detailLoading && detailError) {
    const detalleSinRed = net.isOffline || isOfflineShapedError(detailError);
    return (
      <div {...swipeDetalleCaido} className={PAGINA} data-testid="analytics-detail-error">
        <StickyBackPill
          onClick={volverDelDetalleCaido}
          label={t('analytics.all_documents')}
          testid="back-to-overview-sticky"
          buttonTestid="back-to-overview-btn" />
        {/* EH-1: el veredicto vivo manda sobre la forma del error residual — si
            ahora mismo no hay señal, lo honesto es decir eso. EH-2: y si la
            hubo, el detalle cae al MISMO estado 3 del overview, con su botón,
            que aquí re-pide el detalle de ESTE documento. */}
        <div data-testid="analytics-detail-notice">
          {detalleSinRed ? (
            /* R-3: la misma selección por build que el estado 1 del overview. */
            IS_APP_BUILD ? (
              <RecuadroDeEstado tipo="sin_conexion" texto={t('analytics.offline_empty')} />
            ) : (
              <RecuadroDeEstado tipo="sin_conexion" texto={t('common:offline.no_connection.body')}
                accion={{ texto: t('common:offline.no_connection.cta'), onClick: () => fetchDocAnalytics(selectedDoc), icono: RotateCw, testid: 'analytics-detail-offline-retry-btn' }} />
            )
          ) : (
            <RecuadroDeEstado tipo="no_cargo" texto={t('analytics.load_error.body')}
              accion={{ texto: t('analytics.load_error.cta'), onClick: () => fetchDocAnalytics(selectedDoc), icono: RotateCw, testid: 'analytics-detail-retry-btn' }} />
          )}
        </div>
      </div>
    );
  }

  // ─── DOCUMENT DETAIL VIEW ───
  if (selectedDoc && docAnalytics) {
    const {
      document: doc, progress, has_adaptive_data,
      has_exam_data: servidorTieneExamen, adaptive, exam: examServidor,
    } = docAnalytics;
    // RPW T5 (P3) — la fusión, en el único punto donde se pinta el bloque. En web
    // `entregasDeEsteDoc` es siempre `[]`, la fusión devuelve el mismo objeto y
    // `has_exam_data` no cambia.
    //
    // G-3 — Y NO CORRE SOBRE LA BASE DEL MOTOR. Cuando el detalle vino de la
    // bodega, esa base YA trae las entregas locales fusionadas por el motor:
    // volver a fusionarlas aquí las contaría dos veces. La fusión sigue viva
    // exactamente donde nació —encima de la respuesta VIVA del servidor, que es
    // la que puede ir retrasada respecto del teléfono— y ahí sus dos candados
    // (foto y diario) siguen siendo los que deduplican.
    const entregasDeEsteDoc = DETALLE_DESDE_BODEGA.has(docAnalytics) ? []
      : localDeliveries.filter((d) => (d.document_ids || []).includes(selectedDoc));
    const exam = mergeExamDeliveries(examServidor, entregasDeEsteDoc);
    // Una entrega local es dato de examen aunque el servidor todavía no lo sepa:
    // sin esto, un documento cuyo ÚNICO examen es el local no abriría su pestaña.
    const has_exam_data = servidorTieneExamen || entregasDeEsteDoc.length > 0;
    const showTabs = has_adaptive_data && has_exam_data;
    const hasNoData = !has_adaptive_data && !has_exam_data;

    const readiness = adaptive?.readiness;
    const conReadiness = Boolean(readiness?.has_data);

    return (
      /* ── COS-32 — EL ESTADO VACÍO PINTA YA ACOMODADO ────────────────────────
         Lista y detalle son DOS returns de la MISMA pantalla montada, así que
         React los reconcilia POSICIÓN contra POSICIÓN: sin `key` el `<div>` de
         la lista y el `<div>` del detalle son "el mismo hijo" y NO se monta un
         nodo nuevo, se le cambian las clases al que ya estaba en pantalla, y
         una caja con `transition: all` animaba su layout (padding, borde, fondo y
         radio) a media vista. EL FIX: una `key` distinta en la raíz de cada vista.
         React deja de verlas como el mismo hijo, desmonta una y monta la otra
         entera. Nadie hereda geometría de nadie, así que el primer frame visible
         del estado vacío YA es el definitivo. */
      <div key="detalle" {...swipeDetalle} className={PAGINA} data-testid="analytics-document-detail">
        {/* ── COS-15 — EL REGRESO NO SE PIERDE ARRIBA ────────────────────────
            Al recorrer los temas el botón se iba de pantalla y no había cómo
            volver sin llegar hasta el tope. Ahora se queda: SÓLO el botón, como
            píldora, con el vidrio de la familia del bottom nav.

            PILOTO-1D — LA PÍLDORA SE MUDÓ A `components/StickyBackPill.js`, sin
            cambiarle una clase ni un nodo: el detalle de grupo necesitaba la
            MISMA salida y la alternativa era una copia que se desviaría al
            primer retoque. */}
        <StickyBackPill
          onClick={volverALaLista}
          label={t('analytics.all_documents')}
          testid="back-to-overview-sticky"
          buttonTestid="back-to-overview-btn" />

        {/* Document header + progress.
            COS-17, SIN CAJA: no se puede tocar, y una caja prometía un tap que no
            existe. pantallas-alumno (F, AN3): icono de 40 (personas para la card
            del grupo) y el nombre en 28 (22 en el celular), dos renglones y "...";
            el chip de Readiness a la derecha (en el celular, debajo, alineado con
            el nombre). La caja del encabezado es el ancla de su globo. */}
        <div className="relative" data-testid="doc-header">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className={CAJA_ICONO} aria-hidden="true">
                {esIdDeCard(selectedDoc)
                  ? <Users className={TRAZO_ICONO} />
                  : <GlifoDeMaterial tipo={tipoDeMaterial(doc)} className={TRAZO_ICONO} />}
              </div>
              <h2 className="min-w-0 text-[22px] md:text-[28px] leading-tight font-bold text-[#F4F4F3] line-clamp-2 break-words" title={doc.filename} data-testid="doc-title">{doc.filename}</h2>
            </div>
            {has_adaptive_data && conReadiness ? (
              <div className="pl-[52px] md:pl-0 md:pt-2.5 shrink-0 flex">
                <ChipConGlobo t={t} readiness={readiness} testid="doc-readiness-badge"
                  testidBoton="doc-readiness-badge-btn" testidGlobo="doc-readiness-tip" />
              </div>
            ) : null}
          </div>
          <p className="mt-2 text-[15px] leading-snug text-[#9A9A9A]" data-testid="doc-progress">
            {progress.total_questions > 0 && progress.unique_answered >= progress.total_questions
              ? t('analytics.progress.doc_answered_full', { count: progress.total_questions })
              : t('analytics.progress.doc_answered', { answered: progress.unique_answered, count: progress.total_questions })}
          </p>
        </div>

        {/* Neither mode, empty state (AN10) */}
        {hasNoData && (
          <div data-testid="no-analytics-data">
            {/* ── COS-27 — LAS DOS FRASES NARANJAS SE TOCAN ────────────────────
                Cada una lleva a SU modo en el Centro de Estudio, con ESTE documento
                ya elegido (`/study?doc=<id>&mode=...`, que StudyCenter lee de la
                query). Son frases INLINE: cada una es un bloque en linea con relleno
                táctil compensado por márgenes negativos del mismo tamaño, así el
                alto tocable sube y el texto NO se mueve un pixel.
                COS-14b, el botón de abajo también se lleva el documento. */}
            <RecuadroDeEstado
              tipo="vacio"
              icono={BarChart3}
              titulo={t('analytics.empty.title')}
              texto={(
                <span className="leading-7">
                  <Trans
                    i18nKey="analytics.empty.body"
                    t={t}
                    components={{
                      adaptive: (
                        <button
                          key="adaptive"
                          type="button"
                          onClick={() => navigate(`/study?doc=${selectedDoc}&mode=adaptive`)}
                          className="text-[#F7A06A] font-semibold underline underline-offset-2 inline-block px-1 -mx-1 py-2 -my-2"
                          data-testid="empty-go-adaptive"
                        />
                      ),
                      exam: (
                        <button
                          key="exam"
                          type="button"
                          onClick={() => navigate(`/study?doc=${selectedDoc}&mode=exam`)}
                          className="text-[#F7A06A] font-semibold underline underline-offset-2 inline-block px-1 -mx-1 py-2 -my-2"
                          data-testid="empty-go-exam"
                        />
                      ),
                    }}
                  />
                </span>
              )}
              accion={{ texto: t('analytics.start_studying'), onClick: () => navigate(`/study?doc=${selectedDoc}`), icono: Play, testid: 'start-studying-btn' }}
            />
          </div>
        )}

        {/* Tab switcher, only when BOTH modes have data. pantallas-alumno (F): el control de dos
            botones del Panel (E1), el elegido en blanco. */}
        {showTabs && (
          <PestanasDeDosBotones
            testid="analytics-tabs"
            etiqueta={doc.filename}
            valor={activeTab}
            onCambio={setActiveTab}
            opciones={[
              { valor: 'adaptive', texto: t('analytics.tabs.adaptive'), icono: BookOpen, testid: 'tab-study-progress' },
              { valor: 'exam', texto: t('analytics.tabs.exam'), icono: BarChart3, testid: 'tab-exam-results' },
            ]}
          />
        )}

        {/* ═══ STUDY PROGRESS (Adaptive data) ═══ */}
        {((showTabs && activeTab === 'adaptive') || (!showTabs && has_adaptive_data)) && adaptive && (
          <div className="flex flex-col gap-5" data-testid="study-progress-content">
            {/* Document Exam Readiness, continuous 0-100 bar (ALL plans). COS-14: el botón se
                lleva EL DOCUMENTO (`/study?doc=<id>`, el mecanismo del Panel). */}
            {conReadiness && (
              <TarjetaDeReadiness t={t} readiness={readiness} alEstudiar={() => navigate(`/study?doc=${selectedDoc}`)} />
            )}

            {/* Topic list — always flat (grouped-by-readiness view was removed) */}
            {adaptive.topic_stats.length === 0 ? (
              <RecuadroDeEstado tipo="vacio" icono={BarChart3} texto={t('analytics.no_adaptive_data')} testid="no-adaptive-data" />
            ) : (
              <div className="flex flex-col gap-3" data-testid="topic-list">
                <h3 className="text-[19px] font-semibold text-[#F4F4F3]">{t('analytics.topics_heading')}</h3>
                {adaptive.topic_stats.map((topic, i) => <TarjetaDeTema key={i} topic={topic} i={i} t={t} />)}
              </div>
            )}
          </div>
        )}

        {/* ═══ EXAM RESULTS ═══ */}
        {((showTabs && activeTab === 'exam') || (!showTabs && has_exam_data)) && exam && (
          <ResultadosDeExamen exam={exam} t={t} idioma={i18n.language} />
        )}
      </div>
    );
  }

  // ─── TANDA 16 (#197) — EL DETALLE EN VUELO TIENE SU PROPIA SILUETA ────────
  //
  // La silueta del detalle, que es lo honesto: la forma de lo que viene, en el
  // sitio donde va a aparecer. Detrás de la rama del detalle YA PINTADO (por eso
  // pide `!docAnalytics`) y de las de sin-red y error; delante de la lista. Se
  // apaga sola con el desalojo silencioso de la TANDA 15.
  // pantallas-alumno (F, AN13): copia el detalle nuevo (regreso, título, pestañas,
  // Readiness y temas).
  if (selectedDoc && detailLoading && !docAnalytics) {
    return (
      <div className={PAGINA} data-testid="analytics-detail-loading">
        <EsqueletoDelDetalle />
      </div>
    );
  }

  // ─── OVERVIEW ───
  //
  // TANDA 16 (#198) — Y SI TODAVÍA NO HAY OVERVIEW, SILUETA, NO PANTALLA NEGRA.
  // Esta pantalla monta DORMIDA dentro del carril, `overviewLoading` nace en falso
  // y no vuelve a levantarse, así que al activarse sin datos no había nada que
  // pintar. La silueta es la misma del arranque en frío de arriba.
  if (!overview) {
    return (
      <div className={PAGINA} data-testid="analytics-loading">
        {encabezado}
        <EsqueletoDeAnaliticas />
      </div>
    );
  }

  return (
    /* COS-32 — la gemela de la `key` del detalle (ver el letrero de allá). Las
       dos tienen que existir: una sola no rompe el emparejamiento, porque React
       compara la key vieja contra la nueva y `null` contra `null` sigue siendo
       coincidencia. */
    <div key="lista" className={PAGINA} data-testid="analytics-page">
      {encabezado}

      {/* Engagement Summary: tres números iguales (pieza "Número de resumen"). */}
      <FilaDeResumen testid="engagement-summary">
        {[
          { key: 'documents', label: t('analytics.stats.documents'), value: overview.total_documents },
          { key: 'sessions', label: t('analytics.stats.sessions'), value: overview.total_sessions },
          { key: 'answered', label: t('analytics.stats.answered'), value: overview.total_questions_answered },
        ].map((stat) => (
          <NumeroDeResumen key={stat.key} etiqueta={stat.label} numero={stat.value} testid={`stat-${stat.key}`} />
        ))}
      </FilaDeResumen>

      {/* Document Cards */}
      {overview.doc_cards.length === 0 ? (
        net.isOffline ? (
          /* EH-1, EL VOLTEO, EN EL SITIO (AN17). "Sube un documento y empieza a
             estudiar" es una afirmación sobre LA CUENTA, y sin señal no se puede
             sostener. Voltea aquí y no en toda la pantalla a propósito, los tres
             totales de arriba SÍ son dato leído y el dato se conserva. */
          <div data-testid="analytics-overview-offline">
            <RecuadroDeEstado tipo="sin_conexion" texto={t('analytics.offline_empty')} />
          </div>
        ) : (
          <div data-testid="analytics-overview-empty">
            <RecuadroDeEstado tipo="vacio" icono={BarChart3} texto={t('analytics.overview_empty.body')}
              accion={{ texto: t('analytics.overview_empty.cta'), onClick: () => navigate('/dashboard'), testid: 'go-to-dashboard-btn' }} />
          </div>
        )
      ) : (
        /* pantallas-alumno (F, AN1): rejilla de 2 columnas desde 1024 (1 en tablet y celular) con la
           anatomía fija de la tarjeta del Panel; las tarjetas de una fila miden lo mismo. Toda la
           tarjeta abre el detalle (COS-13: con su apretado y la flecha naranja); el chip de
           Readiness abre su globo sin abrir el detalle (P5).
           R-6, el aterrizaje al tope NO se llama aquí: lo dispara el `useLayoutEffect` de arriba,
           cuando el detalle ya está en el DOM y antes del primer paint. */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="doc-cards">
          {overview.doc_cards.map((card, i) => (
            <TarjetaDeAnaliticas key={card.id} card={card} i={i} t={t} alAbrir={() => setSelectedDoc(card.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
